import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Mail, MapPin, Phone, Send, CheckCircle2, AlertCircle } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

export default function ContactPage() {
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    website_url: '' // Honeypot
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '', website_url: '' });
      } else {
        setStatus('error');
      }
    } catch (error) {
      setStatus('error');
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Kontakt DAPAO | Skriv til os og hør mere</title>
        <meta name="description" content="Har du spørgsmål om medlemskab, donationer eller vores humanitære projekter i Danmark og Palæstina? Kontakt os i dag – vi svarer hurtigt!" />
      </Helmet>

      <Navbar />

      <main className="pt-32 pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-16">
            <h1 className="text-4xl md:text-5xl font-black tracking-tight text-black mb-6 uppercase">
              Kontakt <span className="text-red-600">os</span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Du kan skrive til os om alt, hvad du gerne vil vide om DAPAO. Vi vil hurtigst muligt besvare din forespørgsel.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Kontakt Information */}
            <div className="space-y-12">
              <div className="grid sm:grid-cols-2 gap-8">
                <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-xl shadow-gray-100/50">
                  <div className="mb-6">
                    <MapPin className="text-black h-8 w-8" />
                  </div>
                  <h3 className="text-lg font-bold mb-4 uppercase tracking-wider">Adresse</h3>
                  <address className="not-italic text-gray-600 space-y-1">
                    <p>Amneh Hawwa</p>
                    <p>Bernstorffsvej 86 1.Tv</p>
                    <p>2900 Hellerup</p>
                  </address>
                </div>

                <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-xl shadow-gray-100/50">
                  <div className="mb-6">
                    <Mail className="text-black h-8 w-8" />
                  </div>
                  <h3 className="text-lg font-bold mb-4 uppercase tracking-wider">E-mail</h3>
                  <p className="text-gray-600">dapawomen@gmail.com</p>
                </div>
              </div>

              <div className="bg-black text-white p-8 md:p-10 rounded-3xl shadow-2xl relative overflow-hidden">
                <div className="relative z-10">
                  <h3 className="text-2xl font-bold mb-4 uppercase tracking-wider">Foreningens detaljer</h3>
                  <div className="space-y-4 text-gray-300">
                    <div className="flex justify-between border-b border-white/10 pb-4">
                      <span className="font-medium">Organisation</span>
                      <span>DAPAO</span>
                    </div>
                    <div className="flex justify-between border-b border-white/10 pb-4">
                      <span className="font-medium">CVR-nummer</span>
                      <span>34785708</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium">Type</span>
                      <span>Non-profit</span>
                    </div>
                  </div>
                </div>
                {/* Decorative element */}
                <div className="absolute -right-12 -bottom-12 w-48 h-48 bg-red-600/20 rounded-full blur-3xl" />
              </div>
            </div>

            {/* Kontakt Formular */}
            <div className="bg-white p-8 md:p-10 rounded-3xl border border-gray-200 shadow-xl shadow-gray-100/50">
              {status === 'success' ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 className="h-10 w-10 text-green-600" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4">Tak for din besked!</h3>
                  <p className="text-gray-600 mb-8">Vi har modtaget din henvendelse og vender tilbage til dig så hurtigt som muligt.</p>
                  <button 
                    onClick={() => setStatus('idle')}
                    className="px-8 py-3 bg-black text-white font-bold rounded-full hover:bg-red-600 transition-all uppercase tracking-wider text-sm hover:-translate-y-1"
                  >
                    Send en ny besked
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Honeypot */}
                  <div className="sr-only absolute opacity-0 -z-10">
                    <label htmlFor="website_url">Website URL</label>
                    <input
                      id="website_url"
                      type="text"
                      name="website_url"
                      value={formData.website_url}
                      onChange={handleChange}
                      tabIndex="-1"
                      autoComplete="off"
                    />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label htmlFor="name" className="text-sm font-bold uppercase tracking-wider text-gray-700">Navn</label>
                      <input
                        required
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-600 focus:border-transparent outline-none transition-all"
                        placeholder="Dit fulde navn"
                      />
                    </div>
                    <div className="space-y-2">
                      <label htmlFor="email" className="text-sm font-bold uppercase tracking-wider text-gray-700">E-mail</label>
                      <input
                        required
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-600 focus:border-transparent outline-none transition-all"
                        placeholder="din@email.dk"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="subject" className="text-sm font-bold uppercase tracking-wider text-gray-700">Emne</label>
                    <input
                      required
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-600 focus:border-transparent outline-none transition-all"
                      placeholder="Hvad drejer din henvendelse sig om?"
                    />
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="message" className="text-sm font-bold uppercase tracking-wider text-gray-700">Besked</label>
                    <textarea
                      required
                      id="message"
                      name="message"
                      rows="5"
                      value={formData.message}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-600 focus:border-transparent outline-none transition-all resize-none"
                      placeholder="Skriv din besked her..."
                    ></textarea>
                  </div>

                  {status === 'error' && (
                    <div className="flex items-center gap-3 p-4 bg-red-50 text-red-700 rounded-xl border border-red-100">
                      <AlertCircle className="h-5 w-5 shrink-0" />
                      <p className="text-sm">Der opstod en fejl. Prøv venligst igen senere.</p>
                    </div>
                  )}

                  <button
                    disabled={status === 'loading'}
                    type="submit"
                    className="w-full flex items-center justify-center gap-3 px-8 py-4 bg-black text-white font-bold rounded-xl hover:bg-red-600 disabled:bg-gray-400 transition-all uppercase tracking-wider shadow-lg shadow-gray-200 hover:-translate-y-1"
                  >
                    {status === 'loading' ? (
                      <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        <span>Send besked</span>
                        <Send className="h-5 w-5" />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
